import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Type, Crop as CropIcon, Copy } from 'lucide-react';
import 'pdfjs-dist/web/pdf_viewer.css';

// The MIME used to hand a cropped region to a MediaUploadField drop target (see MediaUploadField).
export const CROP_DND_MIME = 'application/x-kobra-crop';

// Lazy-load pdf.js only when a PDF is actually opened, so it never bloats the initial bundle.
let pdfjsPromise = null;
function loadPdfjs() {
    if (!pdfjsPromise) {
        pdfjsPromise = import('pdfjs-dist/build/pdf.min.mjs').then((pdfjs) => {
            // Bundle the worker as an asset (webpack 5 resolves new URL(..., import.meta.url)).
            pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                'pdfjs-dist/build/pdf.worker.min.mjs',
                import.meta.url
            ).toString();
            return pdfjs;
        });
    }
    return pdfjsPromise;
}

// Crop a rectangle (in CSS px relative to `sourceEl`) out of a canvas/img into a PNG blob.
function cropToBlob(sourceEl, rectCss) {
    const bounds = sourceEl.getBoundingClientRect();
    const isCanvas = sourceEl.tagName === 'CANVAS';
    const natW = isCanvas ? sourceEl.width : sourceEl.naturalWidth;
    const natH = isCanvas ? sourceEl.height : sourceEl.naturalHeight;
    const scaleX = natW / bounds.width;
    const scaleY = natH / bounds.height;
    const sx = Math.max(0, rectCss.x * scaleX);
    const sy = Math.max(0, rectCss.y * scaleY);
    const sw = Math.min(natW - sx, rectCss.w * scaleX);
    const sh = Math.min(natH - sy, rectCss.h * scaleY);
    if (sw < 2 || sh < 2) return Promise.resolve(null);
    const off = document.createElement('canvas');
    off.width = Math.round(sw);
    off.height = Math.round(sh);
    off.getContext('2d').drawImage(sourceEl, sx, sy, sw, sh, 0, 0, off.width, off.height);
    return new Promise((resolve) => off.toBlob((b) => resolve(b), 'image/png'));
}

// A drawable surface (one PDF page canvas, or the image) with an optional crop-drag overlay.
// In crop mode the overlay captures pointer drags; otherwise pointer events fall through so the
// PDF text layer stays selectable.
function CropSurface({ mode, getSource, onCrop, children }) {
    const wrapRef = useRef(null);
    const [drag, setDrag] = useState(null); // { x, y, w, h } in CSS px

    const down = (e) => {
        if (mode !== 'crop' || e.button !== 0) return;
        const b = wrapRef.current.getBoundingClientRect();
        setDrag({ x0: e.clientX - b.left, y0: e.clientY - b.top, x: e.clientX - b.left, y: e.clientY - b.top, w: 0, h: 0 });
        e.preventDefault();
    };
    const move = (e) => {
        if (!drag) return;
        const b = wrapRef.current.getBoundingClientRect();
        const cx = e.clientX - b.left;
        const cy = e.clientY - b.top;
        setDrag((d) => ({ ...d, x: Math.min(d.x0, cx), y: Math.min(d.y0, cy), w: Math.abs(cx - d.x0), h: Math.abs(cy - d.y0) }));
    };
    const up = async () => {
        if (!drag) return;
        const rect = { x: drag.x, y: drag.y, w: drag.w, h: drag.h };
        setDrag(null);
        const src = getSource();
        if (src && rect.w > 2 && rect.h > 2) {
            const blob = await cropToBlob(src, rect);
            if (blob) onCrop(blob);
        }
    };

    return (
        <div
            ref={wrapRef}
            style={{ position: 'relative', width: '100%', cursor: mode === 'crop' ? 'crosshair' : 'auto' }}
            onMouseDown={down}
            onMouseMove={move}
            onMouseUp={up}
            onMouseLeave={() => drag && up()}
        >
            {children}
            {mode === 'crop' && drag && (
                <div
                    style={{
                        position: 'absolute', left: drag.x, top: drag.y, width: drag.w, height: drag.h,
                        border: '2px dashed #7c3aed', background: 'rgba(124,58,237,0.15)', pointerEvents: 'none', zIndex: 5,
                    }}
                />
            )}
        </div>
    );
}

// One rendered PDF page: canvas + selectable text layer, wrapped in a crop surface.
function PdfPage({ pdfjs, page, containerWidth, mode, onCrop }) {
    const canvasRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let task = null;
        (async () => {
            const base = page.getViewport({ scale: 1 });
            const scale = Math.max(0.2, (containerWidth || base.width) / base.width);
            const viewport = page.getViewport({ scale });
            const canvas = canvasRef.current;
            const textDiv = textRef.current;
            if (!canvas || !textDiv) return;
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
            task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
            try {
                await task.promise;
                if (cancelled) return;
                // Text layer for native selection/copy. --scale-factor positions the spans.
                textDiv.innerHTML = '';
                textDiv.style.setProperty('--scale-factor', String(scale));
                textDiv.style.width = `${viewport.width}px`;
                textDiv.style.height = `${viewport.height}px`;
                const textContent = await page.getTextContent();
                if (cancelled) return;
                const tl = new pdfjs.TextLayer({ textContentSource: textContent, container: textDiv, viewport });
                await tl.render();
            } catch (_) {
                /* render cancelled or text layer unsupported — canvas still shows */
            }
        })();
        return () => { cancelled = true; try { task && task.cancel(); } catch (_) { /* noop */ } };
    }, [pdfjs, page, containerWidth]);

    return (
        <div style={{ marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
            <CropSurface mode={mode} getSource={() => canvasRef.current} onCrop={onCrop}>
                <canvas ref={canvasRef} style={{ display: 'block', width: '100%' }} />
                <div
                    ref={textRef}
                    className="textLayer"
                    style={{ position: 'absolute', inset: 0, pointerEvents: mode === 'crop' ? 'none' : 'auto' }}
                />
            </CropSurface>
        </div>
    );
}

// Right-column reference viewer. `doc = { kind:'pdf'|'image', url, name }` (url is a local object
// URL, never uploaded). Lets the admin select/copy text (PDF), crop a region to copy or drag onto
// a left-side image field, scroll, and fit-to-width. Closed only from here (the ✕ button).
export default function SourceDocViewer({ doc, onDoc, onClose }) {
    const [mode, setMode] = useState('select'); // 'select' | 'crop'
    const [pdfPages, setPdfPages] = useState([]);
    const [pdfjs, setPdfjs] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [crop, setCrop] = useState(null); // { url, blob }
    const [width, setWidth] = useState(0);
    const scrollRef = useRef(null);

    // Pick a local file (image or PDF) → object URL (revoke the previous one).
    const pickFile = (file) => {
        if (!file) return;
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        const url = URL.createObjectURL(file);
        onDoc((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { kind: isPdf ? 'pdf' : 'image', url, name: file.name }; });
    };

    // Measure the scroll container so pages/images fit to width.
    useEffect(() => {
        if (!scrollRef.current) return undefined;
        const measure = () => setWidth(scrollRef.current ? scrollRef.current.clientWidth - 24 : 0);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(scrollRef.current);
        return () => ro.disconnect();
    }, [doc]);

    // Load a PDF's pages when the doc is a PDF.
    useEffect(() => {
        let cancelled = false;
        setPdfPages([]);
        if (!doc || doc.kind !== 'pdf') return undefined;
        setLoadingPdf(true);
        (async () => {
            try {
                const lib = await loadPdfjs();
                if (cancelled) return;
                setPdfjs(lib);
                const pdf = await lib.getDocument({ url: doc.url }).promise;
                if (cancelled) return;
                const pages = [];
                for (let i = 1; i <= pdf.numPages; i += 1) pages.push(await pdf.getPage(i));
                if (!cancelled) setPdfPages(pages);
            } catch (_) {
                /* invalid/corrupt PDF — the empty state stays */
            } finally {
                if (!cancelled) setLoadingPdf(false);
            }
        })();
        return () => { cancelled = true; };
    }, [doc]);

    // Clean up crop object URLs.
    useEffect(() => () => { if (crop?.url) URL.revokeObjectURL(crop.url); }, [crop]);

    const onCrop = useCallback((blob) => {
        setCrop((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { blob, url: URL.createObjectURL(blob) }; });
    }, []);

    const copyCrop = async () => {
        if (!crop?.blob || !navigator.clipboard || !window.ClipboardItem) return;
        try { await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': crop.blob })]); } catch (_) { /* clipboard blocked */ }
    };

    const dragCropStart = (e) => {
        if (!crop?.url) return;
        e.dataTransfer.setData(CROP_DND_MIME, crop.url);
        e.dataTransfer.setData('text/uri-list', crop.url);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const closeAll = () => {
        if (doc?.url) URL.revokeObjectURL(doc.url);
        if (crop?.url) URL.revokeObjectURL(crop.url);
        setCrop(null);
        onClose();
    };

    // Empty state — choose a file.
    if (!doc) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 p-3" style={{ border: '2px dashed #d1d5db', borderRadius: 8, minHeight: 300 }}>
                <div className="fw-semibold mb-1" style={{ color: '#5b21b6' }}>Source document</div>
                <p className="text-muted small mb-3">Load the exam PDF or an image to copy text and crop figures into the question fields.</p>
                <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 320 }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; pickFile(f); }}
                />
            </div>
        );
    }

    const btn = (active) => ({
        border: '1px solid #c4b5fd', borderRadius: 6, padding: '0.2rem 0.55rem', fontSize: '0.8rem', fontWeight: 600,
        background: active ? '#7c3aed' : '#fff', color: active ? '#fff' : '#5b21b6', cursor: 'pointer',
    });

    return (
        <div className="d-flex flex-column" style={{ height: '100%', minHeight: 300 }}>
            {/* Toolbar */}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2" style={{ flexShrink: 0 }}>
                <button type="button" style={btn(mode === 'select')} onClick={() => setMode('select')} title="Select & copy text (PDF)">
                    <Type size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Select text
                </button>
                <button type="button" style={btn(mode === 'crop')} onClick={() => setMode('crop')} title="Drag a box to crop a region">
                    <CropIcon size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Crop
                </button>
                <span className="text-muted small text-truncate" style={{ maxWidth: 140 }} title={doc.name}>{doc.name}</span>
                <button type="button" className="btn btn-sm btn-link p-0 ms-auto" onClick={() => document.getElementById('kp-srcdoc-replace').click()} style={{ fontSize: '0.8rem' }}>Replace</button>
                <input id="kp-srcdoc-replace" type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; pickFile(f); }} />
                <button type="button" className="btn btn-sm btn-outline-danger px-2 py-0" onClick={closeAll} title="Close viewer">
                    <X size={16} />
                </button>
            </div>

            {mode === 'crop' && (
                <div className="text-muted small mb-1" style={{ flexShrink: 0 }}>Drag a box over the {doc.kind === 'pdf' ? 'page' : 'image'} to crop it.</div>
            )}

            {/* Scrollable, fit-to-width content */}
            <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', background: '#f1f5f9', borderRadius: 6, padding: 12 }}>
                {doc.kind === 'image' ? (
                    <CropSurface mode={mode} getSource={() => scrollRef.current && scrollRef.current.querySelector('img')} onCrop={onCrop}>
                        <img src={doc.url} alt={doc.name} style={{ display: 'block', width: '100%' }} />
                    </CropSurface>
                ) : loadingPdf ? (
                    <div className="text-muted small p-2">Loading PDF…</div>
                ) : (
                    pdfjs && pdfPages.map((p, i) => (
                        <PdfPage key={i} pdfjs={pdfjs} page={p} containerWidth={width} mode={mode} onCrop={onCrop} />
                    ))
                )}
            </div>

            {/* Crop result chip: copy or drag onto an image field on the left. */}
            {crop && (
                <div className="d-flex align-items-center gap-2 mt-2 p-2" style={{ flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>
                    <img
                        src={crop.url}
                        alt="crop"
                        draggable
                        onDragStart={dragCropStart}
                        title="Drag me onto an image field on the left"
                        style={{ maxHeight: 60, maxWidth: 120, cursor: 'grab', border: '1px solid #c4b5fd', borderRadius: 4 }}
                    />
                    <div className="small text-muted">Drag onto an image field →<br />or</div>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={copyCrop}>
                        <Copy size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Copy
                    </button>
                    <button type="button" className="btn btn-sm btn-link text-muted ms-auto" onClick={() => setCrop(null)}>clear</button>
                </div>
            )}
        </div>
    );
}
